import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type CampaignEconomicsResult, type UnitEconomicsResult } from '@whinerp/client';
import { EmptyState, ErrorState, PageHeading, Panel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-78 Unit economics (G-134).
 *
 * The caveats are not small print here; they are the feature. Every figure on
 * this screen is either a measurement, a projection, or missing, and the
 * screen says which - because "LTV" on a tile gets read as money the company
 * has, and a cost per customer computed from spend nobody recorded gets read
 * as a cost.
 *
 * A missing figure is drawn as a dash with its reason beside it, never as
 * zero and never omitted. An omitted tile reads as "we did not measure this";
 * a zero reads as "this is zero"; and the truth is usually "the data does not
 * support an answer", which is a third thing.
 */

/** A figure, or a dash. Never a zero standing in for an absence. */
function Figure({ value, suffix }: { value: string | number | null; suffix?: string }): JSX.Element {
  if (value === null) {
    return (
      <span className="text-title text-ink-meta" data-figure="none">
        —
      </span>
    );
  }
  return (
    <span className="text-title text-ink" data-figure={String(value)}>
      {String(value)}
      {suffix ? <span className="text-body text-ink-meta">{` ${suffix}`}</span> : null}
    </span>
  );
}

function UnitEconomicsScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();

  const window_ = useMemo(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
    return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
  }, []);

  const economics = useQuery<UnitEconomicsResult, ApiError>({
    queryKey: ['unit-economics', window_.from],
    queryFn: () => api.unitEconomics({ query: window_ }),
  });

  const campaigns = useQuery<CampaignEconomicsResult, ApiError>({
    queryKey: ['campaign-economics', window_.from],
    queryFn: () => api.campaignEconomics({ query: window_ }),
  });

  if (economics.isPending) return <Loading />;
  if (economics.isError) {
    return (
      <div data-component="UnitEconomics">
        <ErrorState
          title={t('state.error')}
          body={errorText(economics.error)}
          onRetry={() => void economics.refetch()}
        />
      </div>
    );
  }

  const figures = economics.data.economics;

  return (
    <div data-component="UnitEconomics" className="flex w-full flex-col gap-4">
      <PageHeading>{t('economics.title')}</PageHeading>
      <p className="text-body text-ink-meta">{t('economics.help')}</p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Panel data-economics-tile="ltv">
          {/* "Projected" in the label itself, not only in a footnote. */}
          <span className="text-meta text-ink-meta">{t('economics.ltv')}</span>
          <div>
            <Figure value={figures.projectedLtv} suffix={figures.currency} />
          </div>
          <span className="text-meta text-ink-meta">{t('economics.ltv.detail')}</span>
        </Panel>

        <Panel data-economics-tile="cac">
          <span className="text-meta text-ink-meta">{t('economics.cac')}</span>
          <div>
            <Figure value={figures.marketingCac} suffix={figures.currency} />
          </div>
          <span className="text-meta text-ink-meta">{t('economics.cac.detail')}</span>
        </Panel>

        <Panel data-economics-tile="ratio">
          <span className="text-meta text-ink-meta">{t('economics.ratio')}</span>
          <div>
            <Figure value={figures.ltvToCac} />
          </div>
          <span className="text-meta text-ink-meta">{t('economics.ratio.detail')}</span>
        </Panel>

        <Panel data-economics-tile="payback">
          <span className="text-meta text-ink-meta">{t('economics.payback')}</span>
          <div>
            <Figure value={figures.paybackMonths} suffix={t('economics.months')} />
          </div>
          <span className="text-meta text-ink-meta">{t('economics.payback.detail')}</span>
        </Panel>
      </div>

      {/* The reasons, beside the numbers rather than under a disclosure. A
          caveat somebody has to open is a caveat nobody reads. */}
      {figures.caveats.length > 0 ? (
        <Panel data-economics-caveats>
          <h2 className="text-body text-ink">{t('economics.caveats')}</h2>
          <ul className="mt-1 flex list-disc flex-col gap-1 ps-5 text-dense text-ink-600">
            {figures.caveats.map((caveat) => (
              <li key={caveat}>{caveat}</li>
            ))}
          </ul>
        </Panel>
      ) : null}

      {campaigns.data && campaigns.data.campaigns.length > 0 ? (
        <Panel data-economics-campaigns>
          <h2 className="text-body text-ink">{t('economics.campaigns')}</h2>
          <p className="text-meta text-ink-meta">{t('economics.campaigns.help')}</p>
          <div className="mt-2 overflow-x-auto" data-scroll-container>
            <table className="w-full text-dense">
              <thead>
                <tr className="text-meta text-ink-meta">
                  <th className="p-2 text-start">{t('economics.column.campaign')}</th>
                  <th className="p-2 text-end">{t('economics.column.cost')}</th>
                  <th className="p-2 text-end">{t('economics.column.deals')}</th>
                  <th className="p-2 text-end">{t('economics.column.perDeal')}</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.data.campaigns.map((campaign) => (
                  <tr
                    key={campaign.campaignId ?? campaign.name}
                    className="border-t border-line"
                    data-economics-campaign={campaign.campaignId ?? campaign.name}
                  >
                    <td className="p-2 text-ink-600">{campaign.name}</td>
                    <td className="p-2 text-end text-ink" data-campaign-cost={campaign.cost ?? 'unknown'}>
                      {/* Blank with a note, not a zero: nobody filled it in. */}
                      {campaign.cost === null ? (
                        <span className="text-ink-meta">{t('economics.cost.unknown')}</span>
                      ) : (
                        `${campaign.cost} ${campaign.currency}`
                      )}
                    </td>
                    <td className="p-2 text-end text-ink">{campaign.dealsWon}</td>
                    <td className="p-2 text-end text-ink-meta">{campaign.costPerDeal ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}
    </div>
  );
}

function Loading(): JSX.Element {
  return (
    <div data-component="UnitEconomics">
      <Skeleton className="h-plot w-full" />
    </div>
  );
}

function Empty(): JSX.Element {
  const t = useT();
  return (
    <div data-component="UnitEconomics">
      <EmptyState title={t('economics.empty')} body={t('economics.empty.help')} />
    </div>
  );
}

function Failed(): JSX.Element {
  const t = useT();
  return (
    <div data-component="UnitEconomics">
      <ErrorState title={t('state.error')} body={t('economics.help')} />
    </div>
  );
}

export const screen = defineScreen({
  id: 'SC-78',
  route: '/revenue/unit-economics',
  surface: 'page',
  title: 'Unit economics',
  features: ['F-080'],
  entitlement: 'F-080',
  reachedBy: [{ kind: 'screen', from: 'SC-77', how: 'Open unit economics from churn and retention' }],
  components: [],
  elements: ['UnitEconomics'],
  states: { empty: Empty, loading: Loading, error: Failed },
  component: UnitEconomicsScreen,
});

export default screen;
